import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

import { ensureCsrfCookie } from "../api/csrf";
import { AUTH_EXPIRED_EVENT, RpcError, rpc, type Policy } from "../api/rpc";
import type { AuthInfo } from "../api/types";
import { AuthContext, type AuthSession, type AuthState } from "./auth-context";

export const authInfoQueryKey = ["auth.info"] as const;

/**
 * `auth.info` is the "who am I" call (note 01 §2.1). A 401 is not an error here:
 * it is the ordinary answer for a signed-out visitor, so it resolves to `null`
 * and the login screen renders.
 */
export async function fetchAuthInfo(): Promise<AuthSession | null> {
  // The app's first call to the server. Anything the client does afterwards is a
  // mutation as far as CSRF is concerned, so the cookie has to exist by now.
  await ensureCsrfCookie();
  try {
    const result = await rpc<AuthInfo>("auth.info");
    return { info: result.data, policies: result.policies };
  } catch (cause) {
    if (cause instanceof RpcError && cause.status === 401) return null;
    throw cause;
  }
}

function indexPolicies(policies: Policy[]): Map<string, Record<string, boolean>> {
  const index = new Map<string, Record<string, boolean>>();
  for (const policy of policies) {
    index.set(policy.id, policy.abilities);
  }
  return index;
}

const noAbilities: Record<string, boolean> = {};

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: authInfoQueryKey,
    queryFn: fetchAuthInfo,
    retry: false,
    staleTime: 30_000,
  });

  // The rpc client fires `auth:expired` whenever any call comes back 401, which
  // happens when a session is revoked in another tab or expires mid-visit.
  useEffect(() => {
    const onExpired = (): void => {
      queryClient.setQueryData(authInfoQueryKey, null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
    };
  }, [queryClient]);

  const session = query.data ?? null;
  const policyIndex = useMemo(
    () => indexPolicies(session === null ? [] : session.policies),
    [session],
  );

  const abilitiesFor = useCallback(
    (id: string | undefined): Record<string, boolean> =>
      (id === undefined ? undefined : policyIndex.get(id)) ?? noAbilities,
    [policyIndex],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: authInfoQueryKey });
  }, [queryClient]);

  const value = useMemo<AuthState>(() => {
    const status: AuthState["status"] = query.isError
      ? "error"
      : query.isPending
        ? "loading"
        : session === null
          ? "anonymous"
          : "authenticated";

    return {
      status,
      session,
      error: query.error,
      abilitiesFor,
      teamAbilities: abilitiesFor(session?.info.team.id),
      refresh,
    };
  }, [abilitiesFor, query.error, query.isError, query.isPending, refresh, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
